import {
  type AnyBlock,
  type FilterConfig,
  type FilterProvider,
  FilterQuery,
  type MutableFilterBlock,
} from '@github-ui/filter'
import {NestedFilterProvider} from '@github-ui/filter/providers'
import {isNestedFilterProvider} from '@github-ui/filter/utils'
import {useMemo} from 'react'

import {
  cleanDisplayName as cleanProviderDisplayName,
  cleanFieldProviderKey,
  extractOrgsFromRawQuery,
  isFieldProviderKey,
  providerMatchesOrgContext,
} from '../utils/org-aware'
import {knownDashboardOrgs} from './issue-field/issue-field-dashboard'

interface UseOrgAwareProvidersOptions {
  baseFilterProviders: Array<FilterProvider | NestedFilterProvider>
  effectiveQuery: string
  isIssueFieldsGlobalSearchEnabled: boolean
}

/**
 * Checks if the providers contain any issue field providers that would benefit from org-aware processing
 */
function hasIssueFieldProviders(providers: Array<FilterProvider | NestedFilterProvider>): boolean {
  return providers.some(provider => {
    if (!isNestedFilterProvider(provider) || !provider.filterProviders) {
      return false
    }

    return provider.filterProviders.some(subProvider => subProvider.key && isFieldProviderKey(subProvider.key))
  })
}

// Converts provider array to record, handling both regular and nested providers
function flattenProvidersToRecord(
  providers: Array<FilterProvider | NestedFilterProvider>,
): Record<string, FilterProvider> {
  const providersRecord: Record<string, FilterProvider> = {}

  for (const provider of providers) {
    if (isNestedFilterProvider(provider)) {
      if (provider.filterProviders) {
        for (const subProvider of provider.filterProviders) {
          const key = subProvider.key
          if (key) {
            providersRecord[key] = subProvider
          }
        }
      }
    } else {
      // Handle regular FilterProvider
      const key = provider.key
      if (key) {
        providersRecord[key] = provider
      }
    }
  }

  return providersRecord
}

// Creates a cleaned provider with modified display name and key
function createCleanedProvider(
  originalProvider: FilterProvider,
  cleanDisplayName: string,
  cleanKey: string,
): FilterProvider {
  // When cleaning a provider key, adjust aliases to prevent duplicate suggestions
  const adjustedAliases = originalProvider.aliases?.filter(alias => alias !== cleanKey) || []

  const cleanedProvider = {
    ...originalProvider,
    displayName: cleanDisplayName,
    key: cleanKey,
    aliases: adjustedAliases,
    // Ensure all methods are properly bound
    getSuggestions: originalProvider.getSuggestions.bind(originalProvider),
    validateFilterBlockValues: originalProvider.validateFilterBlockValues.bind(originalProvider),
    getValueRowProps: originalProvider.getValueRowProps.bind(originalProvider),
  }

  // Override getSuggestions to use cleaned key and displayName
  if ('getSuggestions' in originalProvider && typeof originalProvider.getSuggestions === 'function') {
    const originalGetSuggestions = originalProvider.getSuggestions.bind(originalProvider)

    cleanedProvider.getSuggestions = async function (
      filterQuery: FilterQuery,
      filterBlock: AnyBlock | MutableFilterBlock,
      config: FilterConfig,
      caretIndex?: number | null,
    ) {
      // Temporarily override the original provider's properties
      const savedDisplayName = originalProvider.displayName
      const savedKey = originalProvider.key
      const savedAliases = originalProvider.aliases

      // Set cleaned values on the original provider (cast to mutable for temporary override)
      const mutableProvider = originalProvider as {displayName?: string; key: string; aliases?: string[]}
      mutableProvider.displayName = cleanDisplayName || ''
      mutableProvider.key = cleanKey
      mutableProvider.aliases = adjustedAliases

      try {
        // Create a mock filter block with the cleaned provider key
        if ('provider' in filterBlock && filterBlock.provider) {
          const mockFilterBlock = {
            ...filterBlock,
            provider: {...filterBlock.provider, key: cleanKey},
          } as typeof filterBlock

          return await originalGetSuggestions(filterQuery, mockFilterBlock, config, caretIndex)
        }

        return await originalGetSuggestions(filterQuery, filterBlock, config, caretIndex)
      } finally {
        // Restore original values
        mutableProvider.displayName = savedDisplayName
        mutableProvider.key = savedKey
        mutableProvider.aliases = savedAliases
      }
    }
  }

  return cleanedProvider
}

/**
 * Hook for processing filter providers with org-aware logic.
 *
 * This hook optimizes provider processing by:
 * - Only processing providers when org/repo context actually changes
 * - Early exit when no org context is needed
 *
 * @param options Configuration options for org-aware provider processing
 * @returns Processed providers filtered based on org/repo context
 */
export function useOrgAwareProviders({
  baseFilterProviders,
  effectiveQuery,
  isIssueFieldsGlobalSearchEnabled,
}: UseOrgAwareProvidersOptions): Array<FilterProvider | NestedFilterProvider> {
  return useMemo(() => {
    if (!isIssueFieldsGlobalSearchEnabled) {
      return baseFilterProviders
    }
    // Check if we have issue field providers that would benefit from org-aware processing
    const hasFieldProviders = hasIssueFieldProviders(baseFilterProviders)
    if (!hasFieldProviders) {
      return baseFilterProviders
    }

    // Convert array to record for processing
    const providersRecord = flattenProvidersToRecord(baseFilterProviders)

    if (!effectiveQuery) {
      return Object.values(providersRecord)
    }

    const orgsFromQuery = new Set(extractOrgsFromRawQuery(effectiveQuery))
    // Process with org-aware logic using org names
    return Object.values(processOrgAwareProviders(providersRecord, orgsFromQuery))
  }, [baseFilterProviders, effectiveQuery, isIssueFieldsGlobalSearchEnabled])
}

const processOrgAwareProviders = (
  filterProviders: Record<string, FilterProvider>,
  orgNames: Set<string>,
): Record<string, FilterProvider> => {
  // Early exit if no org filtering needed
  if (orgNames.size === 0) {
    return filterProviders
  }

  // Filter field providers based on org context
  const contextualFilterProviders: Record<string, FilterProvider> = {}

  // Filter and clean field providers based on org context
  for (const [key, provider] of Object.entries(filterProviders)) {
    let cleanedProvider = provider
    if (isFieldProviderKey(key)) {
      // Check if this field provider matches any org in context
      const matchesOrg = providerMatchesOrgContext(key, orgNames)
      if (!matchesOrg) continue

      // Clean display name and key using shared utilities
      const cleanedDisplayName = cleanProviderDisplayName(provider.displayName)
      const cleanedKey = cleanFieldProviderKey(provider.key, orgNames, knownDashboardOrgs)

      // Only create wrapper if something actually changed
      if (cleanedDisplayName !== provider.displayName || cleanedKey !== provider.key) {
        cleanedProvider = createCleanedProvider(provider, cleanedDisplayName, cleanedKey)
      }
    }
    contextualFilterProviders[key] = cleanedProvider
  }

  return contextualFilterProviders
}
